import { TenantModuleFlags } from '../config/tenant';
import { DeviceType, Role, WorkspaceIntent } from '../types';
import { evaluateRouteAccess } from './routeAccess';

export interface WorkspaceResolutionInput {
  role: Role;
  deviceType: DeviceType;
  isRegistered: boolean;
  modules: TenantModuleFlags;
  dispatchBoardEnabled: boolean;
  workspaceOverride?: 'dispatch' | 'portal' | 'crew' | 'wallboard';
}

export interface WorkspaceResolutionResult {
  intent: WorkspaceIntent;
  path: string;
  reason: string;
}

export const resolveWorkspaceLaunch = ({
  role,
  deviceType,
  isRegistered,
  modules,
  dispatchBoardEnabled,
  workspaceOverride,
}: WorkspaceResolutionInput): WorkspaceResolutionResult => {
  const canAccess = (options: {
    allowedRoles?: Role[];
    allowedDeviceTypes?: DeviceType[];
    requiredModules?: Array<keyof TenantModuleFlags>;
  }) => {
    return evaluateRouteAccess({
      role,
      deviceType,
      isRegistered,
      modules,
      allowedRoles: options.allowedRoles,
      allowedDeviceTypes: options.allowedDeviceTypes,
      requiredModules: options.requiredModules,
    }).allowed;
  };

  const canUseDispatchBoard =
    dispatchBoardEnabled &&
    canAccess({
      allowedRoles: [Role.Manager, Role.Admin],
      allowedDeviceTypes: ['base_station'],
      requiredModules: ['clinicalOps', 'dispatch'],
    });

  const canUseCrewMdt = canAccess({
    allowedDeviceTypes: ['ambulance_tablet', 'base_station'],
    requiredModules: ['clinicalOps', 'dispatch'],
  });

  const canUsePersonalPortal = canAccess({
    allowedDeviceTypes: ['byod', 'base_station'],
    requiredModules: ['coreOps'],
  });

  if (workspaceOverride === 'dispatch' && canUseDispatchBoard) {
    return {
      intent: 'dispatch_control',
      path: '/dispatch',
      reason: 'manual_dispatch_override',
    };
  }

  if (workspaceOverride === 'crew' && canUseCrewMdt) {
    return {
      intent: 'crew_mdt',
      path: '/crew-dispatch',
      reason: 'manual_crew_override',
    };
  }

  if (workspaceOverride === 'portal' && canUsePersonalPortal) {
    return {
      intent: 'personal_portal',
      path: '/rota',
      reason: 'manual_portal_override',
    };
  }

  if (workspaceOverride === 'wallboard' && canUseDispatchBoard) {
    return {
      intent: 'wallboard',
      path: '/wallboard',
      reason: 'manual_wallboard_override',
    };
  }

  if (deviceType === 'ambulance_tablet' && canUseCrewMdt) {
    return {
      intent: 'crew_mdt',
      path: '/crew-dispatch',
      reason: 'tablet_default',
    };
  }

  if (canUseDispatchBoard) {
    return {
      intent: 'dispatch_control',
      path: '/dispatch',
      reason: 'dispatch_default',
    };
  }

  if (canUsePersonalPortal) {
    return {
      intent: 'personal_portal',
      path: '/rota',
      reason: 'personal_portal_default',
    };
  }

  return {
    intent: 'admin_setup',
    path: '/documents',
    reason: 'safe_fallback',
  };
};
